import React from 'react';
import axios from 'axios';
import { objectToCamel } from 'ts-case-convert';

import { Settings, PaymentMethodsConfig } from '../@types';
import { PaymentMethodFilter } from '../components/payment-element/payment.types';
import { billingConfigApiToObject } from '../utils/billing';
import { detectDevUrl } from '../utils/detectDevUrl';

const DefaultErrorMessage = 'Failed to retrieve property configuration.';
const apiEndpoint = '/payment/PaymentElement/v2/settings';

type SettingsReturnType = [Settings | null, boolean, string | null];

export function usePaymentElementSettingsV2(
  apiKey: string,
  propertyId: number,
  paymentMethodFilters?: PaymentMethodFilter[],
  baseURL?: string,
): SettingsReturnType {
  const [data, setData] = React.useState<Settings | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!baseURL) return;
    (async () => {
      try {
        /** Base URL to send request to. Only used in development. */
        const requestBaseUrl = detectDevUrl()
          ? (baseURL?.trim()?.replace(/\/$/, '') ?? '') // Clean URLs grabbed from localStorage
          : '';

        // TODO: refactor to avoid explicitly mentioning demo locations.
        //       Check localStorage flag for request type instead.
        const isDemo = !!(
          baseURL.match('unpkg.com') ||
          baseURL.match('cdn.jsdelivr.net') ||
          baseURL.match('payment-element.cloudbeds.com') ||
          baseURL.match('payment-element.cloudbeds-stage.com')
        );

        const { data: response } = isDemo
          ? // Get settings in demo environment
            await axios.get(`${baseURL}${apiEndpoint}`)
          : // Get standard settings
            await axios.post(
              `${requestBaseUrl}${apiEndpoint}`,
              {
                property_id: propertyId,
                origin: window.location.origin,
                payment_method_filters: [...(paymentMethodFilters || [])],
              },
              {
                baseURL,
                headers: {
                  'Content-Type': 'application/json',
                  Authorization: `Bearer ${apiKey}`,
                },
              },
            );
        if (!response.success)
          throw new Error(response.statusMessage || DefaultErrorMessage);

        const settings = {
          billingAddressFields: billingConfigApiToObject(
            response.data.billing_address_fields,
          ),
          mode: (response.data.processors || [{}])
            .filter((el: any) => el.processor)
            .reduce(
              (acc: any, el: any) => ({
                ...acc,
                [el.processor]: el.mode || 'live',
              }),
              {},
            ),
          paymentMethods: response.data.payment_methods,
          paymentMethodConfigurations: Object.keys(
            response.data.payment_method_configurations,
          ).reduce(
            (next, key) => ({
              ...next,
              [key]: objectToCamel(
                response.data.payment_method_configurations[key],
              ),
            }),
            {} as PaymentMethodsConfig,
          ),
          processors: objectToCamel(response.data.processors),
        } as Settings;

        setData(settings);
      } catch (err) {
        setError(err.message || DefaultErrorMessage);
      } finally {
        setLoading(false);
      }
    })();
  }, [apiKey, propertyId, baseURL, paymentMethodFilters]);

  return [data, loading, error];
}
